import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import routes from "routes.js";
import { checkPermission, getUserRole } from "utils/auth";

export default function Admin(props) {
  const { ...rest } = props;
  const [open, setOpen] = useState(true);
  const [currentRoute, setCurrentRoute] = useState("Main Dashboard");
  const userRole = getUserRole();
  const location = useLocation();

  const getActiveRoute = useCallback(
    (routes) => {
      for (const route of routes) {
        if (
          route.layout === "/admin" &&
          location.pathname.includes(`${route.layout}/${route.path}`)
        ) {
          return route.name;
        }
        if (route.children) {
          const childRoute = getActiveRoute(route.children);
          if (childRoute) return childRoute;
        }
      }
      return "Main Dashboard";
    },
    [location.pathname]
  );

  useEffect(() => {
    setCurrentRoute(getActiveRoute(routes));
  }, [location.pathname, getActiveRoute]);

  useEffect(() => {
    const handleResize = () => {
      setOpen(window.innerWidth >= 1200);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getActiveNavbar = (routes) => {
    for (const route of routes) {
      if (
        route.layout === "/admin" &&
        window.location.href.includes(`${route.layout}/${route.path}`)
      ) {
        return route.secondary || false;
      }
      if (route.children) {
        const childNavbar = getActiveNavbar(route.children);
        if (childNavbar) return childNavbar;
      }
    }
    return false;
  };

  const getRoutes = (routes) => {
    return routes.flatMap((route, key) => {
      const hasPermission =
        getUserRole() && checkPermission(route.requiredRole, userRole);

      if (route.layout === "/admin") {
        if (route.children) {
          return route.children.map((childRoute, childKey) => {
            const childPermission = checkPermission(
              childRoute.requiredRole,
              userRole
            );

            if (!childPermission) {
              return (
                <Route
                  path={`/${childRoute.path}`}
                  element={<Navigate to="/forbidden" replace />}
                  key={`${key}-${childKey}`}
                />
              );
            }

            return (
              <Route
                path={`/${childRoute.path}`}
                element={childRoute.component}
                key={`${key}-${childKey}`}
              />
            );
          });
        }
        if (!hasPermission) {
          return (
            <Route
              path={`/${route.path}`}
              element={<Navigate to="/forbidden" replace />}
              key={key}
            />
          );
        }

        return (
          <Route path={`/${route.path}`} element={route.component} key={key} />
        );
      }

      return null;
    });
  };

  if (!userRole) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  return (
    <div className="flex h-full w-full">
      <Sidebar open={open} onClose={() => setOpen(false)} />
      <div className="h-full w-full bg-lightPrimary dark:!bg-navy-900">
        <main
          className={`mx-[12px] h-full flex-none transition-all md:pr-2 xl:ml-[313px]`}
        >
          <div className="h-full">
            <Navbar
              onOpenSidenav={() => setOpen(true)}
              logoText={"ATLAS ADMIN"}
              brandText={currentRoute}
              secondary={getActiveNavbar(routes)}
              {...rest}
            />
            <div className="mx-auto mb-auto h-full min-h-[84vh] p-2 pt-5 md:pr-2">
              <Routes>
                {getRoutes(routes)}
                <Route
                  path="/"
                  element={<Navigate to="/admin/default" replace />}
                />
                <Route
                  path="*"
                  element={<Navigate to="/auth/sign-in" replace />}
                />
              </Routes>
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
